import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { vec2 } from 'gl-matrix';
import {
    levelBoundsOf,
    publishWindowLevelSet,
} from '~/domain/events/windowLevelPublish';
import { WindowLevelDeltaEvent } from '~/domain/events/WindowingDomainEvents';
import { domainEventBus } from '~/domain/events/DomainEventBus';
import { subscribeToWindowLevelEvents } from '~/domain/events/windowLevelSubscription';
import { formatWindowLevelReadout } from '~/domain/dicom/intensityUnits';
import { Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import { buildPresetOptions, WindowLevel } from '~/domain/patient/windowLevel';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { ChangeNotifier } from '~/viewportGrid/adapters/changeSource';
import {
    WindowingAdapter,
    WindowLevelDelta,
    WindowLevelSource,
} from '~/viewportGrid/adapters/types';

/** The registry surface this adapter reads (structural, for tests). */
type RegistryReader = Pick<
    DatasetEnvironmentRegistry,
    'entryFor' | 'subscribeLifecycle'
>;

function sameWindowLevel(
    a: WindowLevel | undefined,
    b: WindowLevel | undefined
): boolean {
    return a?.window === b?.window && a?.level === b?.level;
}

/**
 * Engine-backed {@link WindowingAdapter}: per-series window/level over the
 * domain event bus, so the grid footer and the engine repaint agree on one
 * truth (`Image3D.windowLevel`).
 *
 * Read side: a per-series source that notifies on both bus events (a drag
 * publishes deltas carrying no absolute value, so consumers re-read the
 * domain snapshot) and on registry lifecycle changes — the initial load
 * seeds the window/level directly on the image with no bus event.
 *
 * Write side: a set publishes through the shared publisher, which applies
 * the window as given and clamps nothing; the app's single windowing
 * handler applies it through the registry's Environment that hosts the
 * series.
 */
export class BusWindowingAdapter implements WindowingAdapter {
    private readonly sources = new Map<string, WindowLevelSource>();

    public constructor(private readonly registry: RegistryReader) {}

    public sourceFor(seriesInstanceUid: SeriesInstanceUid): WindowLevelSource {
        const existing = this.sources.get(seriesInstanceUid);
        if (existing) {
            return existing;
        }
        const created = this.createSource(seriesInstanceUid);
        this.sources.set(seriesInstanceUid, created);
        return created;
    }

    /**
     * Sets the series' window as given. A level outside the image's stored
     * range widens that range rather than being moved into it.
     *
     * A window width of zero or less never arrives: `WindowLevel` refuses
     * it when it is made, so nothing is published for it.
     */
    public setWindowLevel(
        seriesInstanceUid: SeriesInstanceUid,
        windowLevel: WindowLevel
    ): void {
        const image = this.imageFor(seriesInstanceUid, 'set');
        if (!image) {
            return;
        }
        publishWindowLevelSet(image, windowLevel.window, windowLevel.level);
    }

    public nudgeWindowLevel(
        seriesInstanceUid: SeriesInstanceUid,
        delta: WindowLevelDelta
    ): void {
        if (!this.imageFor(seriesInstanceUid, 'nudge')) {
            return;
        }
        // The same event a pointer drag on the canvas publishes: the domain
        // adds the step to the series' own window, rounds, and clamps into
        // its stored range.
        domainEventBus.publishSync(
            new WindowLevelDeltaEvent(
                vec2.fromValues(delta.windowWidth, delta.windowCenter),
                seriesInstanceUid,
                'user'
            )
        );
    }

    private createSource(
        seriesInstanceUid: SeriesInstanceUid
    ): WindowLevelSource {
        const readWindowLevel = (): WindowLevel | undefined =>
            this.registry.entryFor(seriesInstanceUid)?.image.windowLevel;
        let unsubscribes: Array<() => void> = [];
        let lastNotified: WindowLevel | undefined;
        const notifier = new ChangeNotifier({
            onFirstSubscribe: () => {
                lastNotified = readWindowLevel();
                unsubscribes = [
                    subscribeToWindowLevelEvents(seriesInstanceUid, () => {
                        lastNotified = readWindowLevel();
                        notifier.notify();
                    }),
                    // Lifecycle fires on every refcount bump; only a
                    // load/unload can change the value without a bus event,
                    // so skip the no-op notifications.
                    this.registry.subscribeLifecycle(() => {
                        const current = readWindowLevel();
                        if (!sameWindowLevel(current, lastNotified)) {
                            lastNotified = current;
                            notifier.notify();
                        }
                    }),
                ];
            },
            onLastUnsubscribe: () => {
                unsubscribes.forEach((unsubscribe) => unsubscribe());
                unsubscribes = [];
            },
        });
        return {
            subscribe: notifier.subscribe,
            getVersion: notifier.getVersion,
            getWindowLevel: readWindowLevel,
            getReadout: () => {
                const image = this.registry.entryFor(seriesInstanceUid)?.image;
                const windowLevel = image?.windowLevel;
                return image && windowLevel
                    ? formatWindowLevelReadout(image, windowLevel)
                    : undefined;
            },
            // Read on demand rather than cached: an MR series derives its
            // auto presets from the whole volume, and that work must not run
            // on every notification. The windows are carried verbatim, never
            // rounded: a DICOM window may be fractional, and a rounded
            // acquisition window would no longer match the window the image
            // is showing.
            getPresets: () => {
                const image = this.registry.entryFor(seriesInstanceUid)?.image;
                return image ? buildPresetOptions(image) : [];
            },
            getResetWindowLevel: () => {
                const image = this.registry.entryFor(seriesInstanceUid)?.image;
                return image?.getResetWindowLevel();
            },
            getLevelRange: () => {
                const image = this.registry.entryFor(seriesInstanceUid)?.image;
                return image ? levelBoundsOf(image) : undefined;
            },
            // The image answers null itself while its volume is still
            // arriving, so an incomplete series and an unknown one read
            // alike: nothing to draw yet.
            getIntensityHistogram: () =>
                this.registry
                    .entryFor(seriesInstanceUid)
                    ?.image.getIntensityHistogram() ?? null,
            getPetMetadata: () => {
                const image = this.registry.entryFor(seriesInstanceUid)?.image;
                return image?.firstSlice.modality === Modality.PT
                    ? image.petMetadata
                    : null;
            },
        };
    }

    private imageFor(
        seriesInstanceUid: SeriesInstanceUid,
        operation: string
    ): Image3D | undefined {
        const image = this.registry.entryFor(seriesInstanceUid)?.image;
        if (!image) {
            console.warn(
                `Window/level ${operation} for an unregistered series was ignored`
            );
        }
        return image;
    }
}
